import React, { useEffect, useState } from "react";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import { fontSize } from "~/styles/tokens";
import { JourneyWhyExpander } from "./JourneyWhyExpander";

const { Title, Paragraph } = Typography;

const LEASE_STEPS = [
  {
    labelKey: "JourneyGuide_Leases_Step_Application" as const,
    bodyKey: "JourneyGuide_Leases_Step_Application_Body" as const,
  },
  {
    labelKey: "JourneyGuide_Leases_Step_Background" as const,
    bodyKey: "JourneyGuide_Leases_Step_Background_Body" as const,
  },
  {
    labelKey: "JourneyGuide_Leases_Step_Offer" as const,
    bodyKey: "JourneyGuide_Leases_Step_Offer_Body" as const,
  },
  {
    labelKey: "JourneyGuide_Leases_Step_MoveIn" as const,
    bodyKey: "JourneyGuide_Leases_Step_MoveIn_Body" as const,
  },
  {
    labelKey: "JourneyGuide_Leases_Step_MoveOut" as const,
    bodyKey: "JourneyGuide_Leases_Step_MoveOut_Body" as const,
  },
];

const STEP_MS = 1700;

interface LeasesJourneySectionProps {
  isMobile: boolean;
}

/**
 * Leases — lifecycle timeline (autoplay when motion is allowed).
 */
export const LeasesJourneySection: React.FC<LeasesJourneySectionProps> = ({
  isMobile,
}) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) {
      return;
    }

    const id = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % LEASE_STEPS.length);
    }, STEP_MS);

    return () => window.clearInterval(id);
  }, []);

  return (
    <div className={`journey-content${isMobile ? " is-mobile" : ""}`}>
      <Title level={isMobile ? 3 : 2} style={{ marginTop: 0 }}>
        {t("JourneyGuide_Leases_Title")}
      </Title>
      <Paragraph
        className="journey-lead"
        style={{ fontSize: isMobile ? fontSize.body : fontSize.emphasis }}
      >
        {t("JourneyGuide_Leases_Description")}
      </Paragraph>

      <ol className={`journey-timeline${isMobile ? " is-stacked" : ""}`}>
        {LEASE_STEPS.map((step, index) => {
          const state =
            index < activeIndex
              ? "is-complete"
              : index === activeIndex
                ? "is-active"
                : "is-pending";
          return (
            <li key={step.labelKey} className={`journey-timeline-step ${state}`}>
              <button
                type="button"
                className="journey-timeline-hit"
                onClick={() => setActiveIndex(index)}
                aria-current={index === activeIndex ? "step" : undefined}
              >
                <span className="journey-timeline-index" aria-hidden>
                  {index + 1}
                </span>
                <span className="journey-timeline-copy">
                  <span className="journey-timeline-label">
                    {t(step.labelKey)}
                  </span>
                  {(isMobile || index === activeIndex) && (
                    <span className="journey-timeline-body">
                      {t(step.bodyKey)}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <JourneyWhyExpander
        summaryKey="JourneyGuide_Leases_Why_Summary"
        detailKey="JourneyGuide_Leases_Why_Detail"
      />
    </div>
  );
};
